/**
 * «Ny familie» i systemadminpanelet (FF-11, #179): navn, familiekode (D5),
 * hjemsted og den første familieadminen (navn, brukernavn). Svaret har en
 * invitasjonslenke som vises én gang; adminen velger passord selv og
 * fullfører oppsettet. Systemadminen ser aldri passord, PIN eller kosthold.
 */
import { useId, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { SystemFamilyCreateBody, type SystemFamilyCreated } from '@stell/shared';
import { Button } from '../../components/primitives';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import { asApiError } from '../../lib/api';
import { isReauthRequired } from './SystemReauthDialog';
import { SystemApi } from './systemApi';

interface Draft {
  name: string;
  code: string;
  lat: string;
  lon: string;
  adminName: string;
  adminUsername: string;
}

const EMPTY: Draft = { name: '', code: '', lat: '', lon: '', adminName: '', adminUsername: '' };

function Field({
  label,
  value,
  onChange,
  testId,
  hint,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testId: string;
  hint?: string;
  inputMode?: 'decimal' | 'text';
}): ReactElement {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <SectionLabel as="label" htmlFor={id} flush>
        {label}
      </SectionLabel>
      <input
        id={id}
        data-testid={testId}
        className="field"
        value={value}
        inputMode={inputMode ?? 'text'}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
        {...(hint ? { 'aria-describedby': `${id}-hint` } : {})}
      />
      {hint ? (
        <span id={`${id}-hint`} className="text-sm text-ink-2">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function CreateFamilyForm({
  guard,
  onCreated,
  onCancel,
}: {
  guard: (action: () => Promise<void>) => Promise<void>;
  onCreated: (created: SystemFamilyCreated) => void;
  onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const set = (key: keyof Draft) => (value: string) => setDraft((d) => ({ ...d, [key]: value }));

  const parsed = useMemo(() => {
    const result = SystemFamilyCreateBody.safeParse({
      name: draft.name,
      code: draft.code,
      householdLat: Number(draft.lat.replace(',', '.')),
      householdLon: Number(draft.lon.replace(',', '.')),
      admin: { name: draft.adminName, username: draft.adminUsername },
    });
    return draft.lat && draft.lon && result.success ? result.data : null;
  }, [draft]);

  async function submit(): Promise<void> {
    if (!parsed || pending) return;
    setPending(true);
    setError(null);
    try {
      await guard(async () => {
        try {
          onCreated(await SystemApi.createFamily(parsed));
        } catch (e) {
          if (isReauthRequired(e)) throw e;
          const err = asApiError(e);
          setError(t(err.code === 'family_code_taken' ? 'system.errors.code_taken' : 'system.errors.generic'));
        }
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <form
      data-testid="system-create-form"
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <h2 className="m-0 text-heading-s font-extrabold text-ink">{t('system.create.title')}</h2>
      <Field label={t('system.create.name')} value={draft.name} onChange={set('name')} testId="system-create-name" />
      <Field
        label={t('system.create.code')}
        value={draft.code}
        onChange={set('code')}
        testId="system-create-code"
        hint={t('system.code_hint')}
      />
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('system.create.lat')} value={draft.lat} onChange={set('lat')} testId="system-create-lat" inputMode="decimal" />
        <Field label={t('system.create.lon')} value={draft.lon} onChange={set('lon')} testId="system-create-lon" inputMode="decimal" />
      </div>
      <span className="-mt-2 text-sm text-ink-2">{t('system.create.place_hint')}</span>
      <Field
        label={t('system.create.admin_name')}
        value={draft.adminName}
        onChange={set('adminName')}
        testId="system-create-admin-name"
      />
      <Field
        label={t('system.create.admin_username')}
        value={draft.adminUsername}
        onChange={set('adminUsername')}
        testId="system-create-admin-username"
        hint={t('system.create.admin_hint')}
      />
      <p className="m-0 rounded-md bg-warn-bg px-3.5 py-3 text-sm font-bold text-ink">{t('system.create.code_warning')}</p>
      {error ? (
        <p role="alert" data-testid="system-create-error" className="m-0 text-sm text-bad">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-3">
        <Button variant="ghost" onClick={onCancel} data-testid="system-create-cancel">
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={!parsed || pending} data-testid="system-create-submit">
          {t('system.create.submit')}
        </Button>
      </div>
    </form>
  );
}
